import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { portalApi, loadRazorpay, rupees, type PaymentHistoryRow, type StartPay } from '../lib/portalApi';
import { ApiError, apiUrl, resolveTenantSlug, tokens } from '../lib/api';
import { useT } from '../context/I18nContext';

/**
 * The parent's PAYMENT LIST — every fee transaction across all their children and academic years, the
 * way a parent expects to see it (like the Sri Chaitanya payment history): transaction ref, amount,
 * fee type, year, gateway, status, receipt. Unpaid/partly-paid invoices show as "Due" rows with a
 * Pay now button that reuses the verified Razorpay flow.
 */
export function PaymentsPage() {
  const t = useT();
  const [rows, setRows] = useState<PaymentHistoryRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [paying, setPaying] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(() => {
    portalApi.paymentHistory()
      .then(setRows)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load payments.'));
  }, []);
  useEffect(load, [load]);

  const pay = async (row: PaymentHistoryRow) => {
    setPaying(row.invoiceId);
    try {
      const ok = await loadRazorpay();
      if (!ok) { alert('Could not load the payment window. Check your connection.'); return; }
      const order: StartPay = await portalApi.startPay(row.invoiceId);
      const rzp = new (window as unknown as { Razorpay: new (o: unknown) => { open: () => void } }).Razorpay({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency,
        name: 'School fees',
        description: `${row.feeType} · ${order.invoiceNumber}`,
        order_id: order.orderId,
        handler: async (resp: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await portalApi.verifyPay({
              invoiceId: row.invoiceId, amountPaise: order.amountPaise, orderId: resp.razorpay_order_id,
              paymentId: resp.razorpay_payment_id, signature: resp.razorpay_signature,
            });
            load();
          } catch { alert('Payment could not be verified. If money was deducted, it will be reversed.'); }
        },
        theme: { color: '#C1272D' },
      });
      rzp.open();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Could not start the payment.');
    } finally { setPaying(null); }
  };

  /**
   * Open the printable receipt.
   *
   * ⚠ THREE things were wrong here and all three produced the same "it doesn't work":
   *
   *  1. It called /api/fees/... — the STAFF finance route. A parent is not TrustAdmin,
   *     CampusAdmin or Accountant, so their own receipt came back 403.
   *  2. It saved the response as `.pdf`. The endpoint returns HTML (and previously JSON), so
   *     even for an accountant the file was a mislabelled blob that no reader would open.
   *  3. It downloaded rather than displayed, so nobody ever saw the failure — just a broken file.
   *
   * Now: the portal route, gated by the guardian link, opened in a tab. Every browser has
   * "Save as PDF" in its print dialog, so the parent gets a PDF without us shipping a PDF engine.
   */
  const downloadReceipt = async (row: PaymentHistoryRow) => {
    if (!row.paymentId) return;
    try {
      const slug = resolveTenantSlug();
      const res = await fetch(apiUrl(`/api/portal/invoices/${row.invoiceId}/receipt/${row.paymentId}`), {
        headers: { ...(slug ? { 'X-Tenant': slug } : {}), ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}) },
      });
      if (!res.ok) throw new Error();
      const html = await res.text();

      // ⚠ Written into a blob URL rather than document.write on a fresh window: a popup blocker
      // stops window.open() with no content far less often than it stops a written document, and
      // this way the receipt keeps its own origin-less context.
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const w = window.open(url, '_blank');
      if (!w) { alert('Please allow pop-ups to view the receipt.'); }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { alert('Could not open the receipt.'); }
  };

  if (err) return <div className="card" style={{ padding: 20 }}>{err}</div>;
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>{t('Loading…')}</p>;

  const years = Array.from(new Set(rows.map((r) => r.academicYear))).sort().reverse();
  const types = Array.from(new Set(rows.map((r) => r.feeType))).sort();
  const filtered = rows.filter((r) =>
    (!yearFilter || r.academicYear === yearFilter) && (!typeFilter || r.feeType === typeFilter));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{t('Fees')}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{t('Payments')}</h1>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select className="input" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('All years')}</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className="input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">{t('All fee types')}</option>
          {types.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
        </select>
      </div>

      {filtered.length === 0
        ? <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>{t('No payments yet')}</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((r, i) => {
              const due = r.status === 'Due' || r.status === 'Partly paid';
              return (
                <div key={i} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px' }}>
                    <div style={{ fontWeight: 600 }}>{r.feeType} <span style={{ fontWeight: 400, color: 'var(--ink-3)', fontSize: 13 }}>· {r.academicYear}</span></div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'monospace' }}>{r.transactionRef}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.studentName} · {formatDate(r.on)}</div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{rupees(r.amountPaise)}</div>
                  <StatusPill status={r.status} />
                  <div style={{ minWidth: 110, textAlign: 'right' }}>
                    {due
                      ? <button className="btn btn-accent" disabled={paying === r.invoiceId} onClick={() => pay(r)}>
                          {paying === r.invoiceId ? '…' : t('Pay now')}
                        </button>
                      : r.receiptAvailable
                        ? <button className="btn" onClick={() => downloadReceipt(r)}>{t('Receipt')}</button>
                        : <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>—</span>}
                  </div>
                </div>
              );
            })}
          </div>}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const t = useT();
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    Success: { bg: '#e7f6ec', fg: '#1a7f37', label: t('Paid') },
    Due: { bg: '#fdecec', fg: '#c1272d', label: t('Due') },
    'Partly paid': { bg: '#fff4e5', fg: '#b26a00', label: t('Partly paid') },
  };
  const s = map[status] ?? { bg: 'var(--surface-2,#eee)', fg: 'var(--ink-2)', label: status };
  return <span style={{ background: s.bg, color: s.fg, padding: '3px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{s.label}</span>;
}
